import {Toaster} from 'react-hot-toast';
import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {motion} from 'framer-motion';
import {ContactDrawer} from '../ContactDrawer';
import {Head} from './head';
import Header from '@/components/Shared/Header';
import Footer from '@/components/Landing/Footer';

export default function DefaultLayout({
                                        children,
                                        onMenuClick,
                                        forceHeaderLight = false,
                                        hideHeader = false,
                                        hideFooter = false,
                                      }: {
  children: React.ReactNode;
  onMenuClick?: () => void;
  forceHeaderLight?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  const closeDrawer = () => setIsOpen(false);
  
  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  // Allow children to trigger opening the contact drawer via a custom event
  useEffect(() => {
    const handler: EventListener = () => setIsOpen(true);
    // Using a custom DOM event keeps coupling low between layout and pages/components
    // @ts-expect-error custom event name not in lib.dom types
    window.addEventListener('open-contact', handler);
    return () => {
      // @ts-expect-error custom event name not in lib.dom types
      window.removeEventListener('open-contact', handler);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isOpen]);

  const drawerLayer = (
    <>
      {/* Contact drawer overlay */}
      {isOpen && (
        <motion.div
          animate={{opacity: 1}}
          className="fixed inset-0 bg-black bg-opacity-90 backdrop-blur-sm z-[399]"
          exit={{opacity: 0}}
          initial={{opacity: 0}}
          transition={{duration: 0.5}}
          onClick={closeDrawer}
        />
      )}
      <ContactDrawer closeDrawer={closeDrawer} isOpen={isOpen}/>
    </>
  );

  // Keyhole menu removed; ignore onMenuClick and hide menu button

  return (
    <>
      <Head/>
      {portalTarget ? createPortal(drawerLayer, portalTarget) : drawerLayer}
      
      {/* Header - fixed at top (can be hidden for page variants) */}
      {!hideHeader && (
        <Header 
          showMenuButton={false}
          animate={true}
          forceLight={forceHeaderLight}
        />
      )}
      
      {/* Main content */}
      <Toaster position="top-center"/>
      {children}
      {!hideFooter && <Footer/>}
    </>
  );
}
